import {
  Box,
  Button,
  Stack,
  Text,
  Theme,
  SxProps,
  styled,
} from '@ltvco/refresh-lib/theme';
import { isZeroed, nameize } from '@ltvco/refresh-lib/utils';
import { Record as RecordType, UseReportResult } from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  CollectionType,
  PersonSearchParamsv2,
} from '@ltvco/refresh-lib/v2';
const Image = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: 300,
  borderRadius: 8,
  objectFit: 'contain',
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

import {
  PdfPreviewBox,
  SummaryComponent,
  MessageTeaser,
  ReportSelectionPills,
  ReportSkeleton,
  TeaserSkeleton,
  CourtCaseMessageContent,
  TryWebSearchButton,
} from 'components/ChatBV/components';
import { SEARCH_TYPES, TRIGGER_SEARCH } from 'components/ChatBV/constants';
import {
  MessageReportData,
  PersonParamsFunctionArguments,
  PossibleReportParams,
} from 'components/ChatBV/types';
import { renderMessageContent } from 'components/ChatBV/utils';
import { OpinionResult } from 'routes/Dashboard/CourtCases/v2/types';

import { CourtOpinionsPage } from './CourtOpinions/CourtOpinionsPage';
import {
  PropertySummaryData,
  EmailSummaryData,
  VehicleReportSummaryData,
} from './SummaryComponent/DataTypes/SummaryTypes';

/**
 * Check if the report data is null to show the zeroed summary
 */
const reportNullData = (
  reports: MessageReportData[],
  searchType?: string
): boolean => {
  const entities = reports?.[0]?.reportData?.entities;

  switch (searchType) {
    case SEARCH_TYPES.PROPERTY:
      return entities?.properties?.length === 0;
    case SEARCH_TYPES.EMAIL:
    case SEARCH_TYPES.PHONE:
    case SEARCH_TYPES.USERNAME:
    case SEARCH_TYPES.CONTACT:
    case SEARCH_TYPES.PERSON:
      return entities?.people?.length === 0;
    case SEARCH_TYPES.VEHICLE:
      return entities?.vehicles?.automobiles?.length === 0;
    default:
      return false;
  }
};

export const MessageContent = ({
  content,
  isHeader,
  isSticky,
  hasUserScrolled,
  handleButtonClick,
  handleChatTeaserClick,
  handleFiltersClick,
  ContainerStyles,
  teaser,
  params,
  reports,
  currentBvid,
  currentPermalink,
  reportOpen,
  searchData,
  collectionType,
  setCollectionType,
  reportSummaryData,
  searchType,
  executedFunctionCall,
  imageUrl,
  isPdf,
  possibleReports,
  onReportSelected,
  isLoadingReport,
  isLoadingTeaser,
  teaserSelectionMessage,
  teaserSearchType,
  courtCaseData,
  courtOpinionData,
  courtOpinionCaseName,
  opinionSummaryContextId,
}: {
  content: string;
  isHeader: boolean;
  isSticky: boolean;
  hasUserScrolled: boolean;
  // eslint-disable-next-line no-unused-vars
  handleButtonClick: (report: MessageReportData) => void;
  // eslint-disable-next-line no-unused-vars
  handleChatTeaserClick: (bvid: string) => void;
  handleFiltersClick: (params: PersonSearchParamsv2) => void;
  ContainerStyles?: SxProps<Theme>;
  teaser: RecordType[] | undefined;
  params: PersonParamsFunctionArguments | undefined;
  reports: MessageReportData[] | undefined;
  messagePermalink: string | undefined;
  currentBvid: string | undefined;
  currentPermalink: string | undefined;
  reportOpen: boolean;
  searchData: AdvancedSearchURLParams;
  collectionType: CollectionType;
  setCollectionType: (collectionType: CollectionType) => void;
  reportSummaryData?: {
    property?: PropertySummaryData[];
    email?: EmailSummaryData[];
    vehicle?: VehicleReportSummaryData[];
    username?: Array<Record<string, any>>;
    // TODO: Add other report types here
  };
  searchType?: string;
  executedFunctionCall?: string;
  imageUrl?: string;
  isPdf?: boolean;
  possibleReports?: PossibleReportParams[];
  onReportSelected?: (report: PossibleReportParams) => void;
  isLoadingReport?: boolean;
  isLoadingTeaser?: boolean;
  teaserSelectionMessage?: string; // Friendly message to display for teaser selections
  teaserSearchType?: string; // search_type from smartsearchbot (person, court_cases, obituary, etc.)
  courtCaseData?: any;
  courtOpinionData?: OpinionResult;
  courtOpinionCaseName?: string; // Case name from metadata for display when restoring from history
  opinionSummaryContextId?: string; // Context ID from opinion summary response for restoration
}) => {
  const wasTriggerSearchExecuted =
    reports &&
    reports.length > 0 &&
    // Explicitly exclude GET_SPECIFIC_REPORT_SECTION
    executedFunctionCall !== 'get_specific_report_section' &&
    // Only show if it's TRIGGER_SEARCH or undefined (old messages with property/email)
    (executedFunctionCall === TRIGGER_SEARCH ||
      (executedFunctionCall === undefined &&
        (searchType === SEARCH_TYPES.PROPERTY ||
          searchType === SEARCH_TYPES.EMAIL ||
          searchType === SEARCH_TYPES.PHONE ||
          searchType === SEARCH_TYPES.VEHICLE ||
          searchType === SEARCH_TYPES.PERSON ||
          searchType === SEARCH_TYPES.OBITUARY ||
          searchType === SEARCH_TYPES.CONTACT ||
          searchType === SEARCH_TYPES.ANCESTOR ||
          searchType === SEARCH_TYPES.USERNAME ||
          searchType === SEARCH_TYPES.COURTCASES ||
          searchType === SEARCH_TYPES.COURT_OPINIONS)));

  // Don't show summary or teaser for court opinion messages
  // Check multiple indicators to ensure we catch all court opinion messages
  const isCourtOpinionMessage =
    content === 'COURT_OPINION_PAGE' ||
    !!courtOpinionData ||
    !!courtOpinionCaseName ||
    !!opinionSummaryContextId;

  const isSearchTypeSummary =
    !isHeader &&
    !isCourtOpinionMessage && // Don't show summary for court opinion messages
    (wasTriggerSearchExecuted || teaser?.length) &&
    (searchType === SEARCH_TYPES.PROPERTY ||
      searchType === SEARCH_TYPES.EMAIL ||
      searchType === SEARCH_TYPES.PHONE ||
      searchType === SEARCH_TYPES.VEHICLE ||
      searchType === SEARCH_TYPES.PERSON ||
      searchType === SEARCH_TYPES.OBITUARY ||
      searchType === SEARCH_TYPES.CONTACT ||
      searchType === SEARCH_TYPES.ANCESTOR ||
      searchType === SEARCH_TYPES.USERNAME ||
      searchType === SEARCH_TYPES.COURTCASES ||
      searchType === SEARCH_TYPES.COURT_OPINIONS);

  const hasReportSummaryData =
    reportSummaryData &&
    Object.values(reportSummaryData).some(
      (reportArray) => Array.isArray(reportArray) && reportArray.length > 0
    );

  // Show summary ONLY if TRIGGER_SEARCH was executed (not GET_SPECIFIC_REPORT_SECTION),
  // it's property/email/phone/vehicle, and we have summary data
  // BUT NOT for court opinion messages
  const shouldShowSummary =
    !isCourtOpinionMessage && // Don't show summary for court opinion messages
    isSearchTypeSummary &&
    (hasReportSummaryData || teaser?.length);

  const reportData = reports?.[0]?.reportData;
  const isZeroedFromApi =
    isZeroed({ data: reportData } as UseReportResult) === true;
  const isReportNullState = reportNullData(reports ?? [], searchType);

  const shouldShowZeroedSummary = Boolean(
    !isCourtOpinionMessage &&
      isSearchTypeSummary &&
      !teaser?.length &&
      (isZeroedFromApi || isReportNullState)
  );

  // Only hide the original content when we actually show a summary
  const shouldHideContent =
    shouldShowSummary || shouldShowZeroedSummary || courtCaseData;

  // For person reports, show button if TRIGGER_SEARCH was executed and we have reports
  const shouldShowPersonButton =
    !shouldShowSummary &&
    !shouldShowZeroedSummary &&
    !isHeader &&
    searchType === SEARCH_TYPES.PERSON &&
    reports &&
    reports.length > 0;
  return (
    <Stack spacing={1} sx={ContainerStyles} mb={2}>
      {isLoadingReport ? (
        <ReportSkeleton />
      ) : shouldShowSummary || shouldShowZeroedSummary ? (
        <>
          <SummaryComponent
            reportData={reportSummaryData || {}}
            teaser={teaser}
            params={params}
            currentPermalink={currentPermalink ?? ''}
            handleButtonClick={handleButtonClick}
            reportsAssociated={reports ?? []}
            shouldShowZeroedSummary={shouldShowZeroedSummary}
          />
        </>
      ) : shouldHideContent ? null : (
        <Box>
          {imageUrl && (
            <Box
              sx={{
                marginBottom: 2,
                display: 'inline-block',
              }}
            >
              {isPdf ? (
                <PdfPreviewBox size={120} iconSize={48} />
              ) : (
                <Image src={imageUrl} alt="Uploaded image" />
              )}
            </Box>
          )}
          {content === 'COURT_OPINION_PAGE' ? (
            <CourtOpinionsPage
              item={courtOpinionData}
              caseName={courtOpinionCaseName}
              existingOpinionSummaryContextId={opinionSummaryContextId}
            />
          ) : (
            <Text
              variant={isHeader ? 'h1' : 'body1'}
              sx={{
                fontWeight: isHeader ? 600 : 400,
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
                marginBottom: isHeader ? '0' : '20px',
                marginTop: isHeader ? '0' : '10px',
                lineHeight: 1.5,
                fontSize: isHeader
                  ? isSticky && hasUserScrolled
                    ? '24px !important'
                    : '30px !important'
                  : undefined,
                transition: isHeader ? 'font-size 0.3s ease-in-out' : undefined,
                '& *': {
                  fontSize: 'inherit !important',
                },
              }}
            >
              {renderMessageContent(teaserSelectionMessage || content)}
            </Text>
          )}
          {possibleReports &&
            possibleReports.length > 1 &&
            onReportSelected && (
              <ReportSelectionPills
                possibleReports={possibleReports}
                onReportSelected={onReportSelected}
              />
            )}
          {shouldShowPersonButton &&
            reports &&
            (reports.length === 1 ? (
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleButtonClick(reports[0])}
                  disabled={currentPermalink === reports[0].permalink}
                >
                  View full report
                </Button>
              </Box>
            ) : (
              <Box>
                {reports.map((report) => (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleButtonClick(report)}
                    disabled={currentPermalink === report.permalink}
                    key={report.permalink}
                  >
                    {nameize(report.type)}
                  </Button>
                ))}
              </Box>
            ))}
        </Box>
      )}
      {isLoadingTeaser ? (
        <TeaserSkeleton />
      ) : (
        teaser && (
          <MessageTeaser
            teaser={teaser}
            params={params}
            currentBvid={currentBvid || ''}
            reportOpen={reportOpen}
            searchData={searchData}
            collectionType={collectionType}
            setCollectionType={setCollectionType}
            teaserSearchType={teaserSearchType}
            handleChatTeaserClick={handleChatTeaserClick}
            handleFiltersClick={handleFiltersClick}
          />
        )
      )}
      {courtCaseData && (
        <CourtCaseMessageContent courtCaseData={courtCaseData} />
      )}
      {!isHeader && <TryWebSearchButton />}
    </Stack>
  );
};
